import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { verifyCollector, submitInquiry } from "@/lib/api";
import Reveal from "@/components/Reveal";

const bts = [
  { image: "/artworks/six-wise-guys.jpg", title: "Six Wise Guys", note: "Studio floor, 2021." },
  { image: "/artworks/universal-race.jpg", title: "Universal Race", note: "Panel detail, oil on hardboard." },
  { image: "/artworks/sign-from-above.jpg", title: "Sign From Above", note: "Final state, before varnish." },
];

export default function Collector() {
  const [password, setPassword] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [checking, setChecking] = useState(false);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!password.trim()) return;
    setChecking(true);
    try {
      const res = await verifyCollector(password.trim());
      if (res.ok) {
        setUnlocked(true);
      } else {
        toast("That is not the key.");
      }
    } catch {
      toast("Something went quiet. Please try again.");
    } finally {
      setChecking(false);
    }
  };

  const handleInquiry = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) {
      toast("Please share your name, email, and a note.");
      return;
    }
    setSending(true);
    try {
      await submitInquiry({
        name: name.trim(),
        email: email.trim(),
        message: message.trim(),
      });
      toast("Received. We will write back soon.");
      setName("");
      setEmail("");
      setMessage("");
    } catch {
      toast("We could not deliver your note. Please try again.");
    } finally {
      setSending(false);
    }
  };

  if (!unlocked) {
    return (
      <main
        data-testid="collector-gate"
        className="bone-section min-h-[100svh] flex items-center justify-center px-6 md:px-12"
      >
        <div className="max-w-md w-full">
          <p className="hairline opacity-60 mb-10 text-center">
            Private
          </p>
          <h1 className="font-serif italic text-4xl md:text-5xl leading-tight text-center">
            The collector's room.
          </h1>
          <p className="font-serif italic text-lg md:text-xl opacity-70 mt-6 text-center">
            By invitation.
          </p>

          <form onSubmit={handleVerify} className="mt-16">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="invisible-input text-center"
              placeholder="Key"
              data-testid="collector-password-input"
              autoFocus
            />
            <div className="mt-10 flex items-center justify-between">
              <Link
                to="/"
                className="hairline opacity-50 hover:opacity-90 transition-opacity duration-700"
                data-testid="collector-back-link"
              >
                ← Return
              </Link>
              <button
                type="submit"
                disabled={checking}
                className="link-quiet font-serif italic text-lg disabled:opacity-40"
                data-testid="collector-submit-button"
              >
                {checking ? "…" : "Enter"}
              </button>
            </div>
          </form>
        </div>
      </main>
    );
  }

  return (
    <main
      data-testid="collector-area"
      className="bone-section min-h-[100svh]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 pt-24 md:pt-32 pb-32 md:pb-48">
        <div className="flex items-center justify-between mb-16">
          <p className="hairline opacity-60" data-testid="collector-welcome">
            Private · Chapter II
          </p>
          <Link
            to="/"
            className="hairline opacity-50 hover:opacity-90 transition-opacity duration-700"
          >
            ← Return
          </Link>
        </div>

        <Reveal className="max-w-2xl mb-24">
          <h1 className="font-serif italic text-5xl md:text-6xl leading-[1.05]">
            Originals & unseen work.
          </h1>
          <p className="font-serif italic text-xl md:text-2xl opacity-70 mt-6">
            Available by conversation. Each painting is unique, oil & household
            paints on hardboard.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16 mb-32">
          {bts.map((item, i) => (
            <Reveal key={item.title} delay={i * 120}>
              <figure data-testid={`collector-bts-${i}`}>
                <img
                  src={item.image}
                  alt={item.title}
                  loading="lazy"
                  className="w-full h-auto block"
                />
                <figcaption className="mt-4">
                  <p className="font-serif italic text-xl">{item.title}</p>
                  <p className="hairline opacity-50 mt-1">{item.note}</p>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>

        <div className="rule opacity-30 mb-16" />

        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-24">
          <Reveal className="md:col-span-5">
            <p className="hairline opacity-60 mb-6">Enquire</p>
            <h2 className="font-serif italic text-3xl md:text-4xl leading-[1.1]">
              Write a short note.
            </h2>
            <p className="font-serif italic text-lg opacity-70 mt-4">
              We reply personally, usually within a week.
            </p>
          </Reveal>

          <form
            onSubmit={handleInquiry}
            className="md:col-span-7 space-y-8"
            data-testid="collector-inquiry-form"
          >
            <div>
              <label className="hairline opacity-60 block mb-2">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="invisible-input"
                data-testid="inquiry-name-input"
                autoComplete="name"
              />
            </div>
            <div>
              <label className="hairline opacity-60 block mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="invisible-input"
                data-testid="inquiry-email-input"
                autoComplete="email"
              />
            </div>
            <div>
              <label className="hairline opacity-60 block mb-2">Message</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                className="invisible-input resize-none"
                data-testid="inquiry-message-input"
              />
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={sending}
                className="link-quiet font-serif italic text-lg disabled:opacity-40"
                data-testid="inquiry-submit-button"
              >
                {sending ? "Sending…" : "Send"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </main>
  );
}
